import { Button, Card, Tooltip } from '~src/components/display';
import { useLocalization, useOfferProductState } from '~src/hooks';
import { useSolarPackages } from '~src/hooks/services';
import { calculateOfferPrice, withVAT } from '~src/utilities/calculationHelper';
import { deepNullToUndefined } from '~src/utilities/convert';
import { formatCurrency } from '~src/utilities/format';

import { useCombinePackageWithSolarExpenses } from '../../hooks/useCombinePackageWithSolarExpenses';

import { solarPackage, solarPackageDiscount, solarPackageProduct, solarPackageTitle } from './solarPackage.module.css';

type SolarPackageProps = {
  solarProductPackage: ReturnType<typeof useSolarPackages>['packages'][number];
  onSelect: (packageID: number) => void;
};

export const SolarPackage = ({ solarProductPackage, onSelect }: SolarPackageProps) => {
  const translate = useLocalization();
  const {
    selectedBattery,
    selectedSolarPanel,
    selectedInverter,
    selectedDiscount,
    selectedDiscountWithoutInstallation,
  } = useOfferProductState();

  const { packageID, title, solarPanel, inverter, batteries, discount, discountWithoutInstallation } =
    solarProductPackage;

  const battery = batteries?.[0];

  const onSelectPackage = () => onSelect(packageID);

  const { withInstallation: solarPackageWithInstallation, withoutInstallation: solarPackageWithoutInstallation } =
    useCombinePackageWithSolarExpenses(solarProductPackage);

  const priceWithInstallation = formatCurrency(
    withVAT(calculateOfferPrice(deepNullToUndefined(solarPackageWithInstallation)))
  );

  const priceWithoutInstallation = formatCurrency(
    withVAT(calculateOfferPrice(deepNullToUndefined(solarPackageWithoutInstallation)))
  );

  const selected =
    selectedSolarPanel?.id === solarPanel?.id &&
    selectedSolarPanel?.quantity === solarPanel?.quantity &&
    selectedInverter?.id === inverter?.id &&
    selectedBattery?.id === battery?.id &&
    (selectedDiscount ?? 0) === (discount ?? 0) &&
    (selectedDiscountWithoutInstallation ?? 0) === (discountWithoutInstallation ?? 0);

  return (
    <Card key={packageID}>
      <div className={solarPackage}>
        <h1 className={solarPackageTitle}>{title ?? `Pakke ${packageID}`}</h1>

        <div className={solarPackageProduct}>
          <b>{solarPanel.quantity} x</b> {solarPanel.brand} {solarPanel.model}
        </div>

        <div className={solarPackageProduct}>
          <b>1 x</b> {inverter.brand} {inverter.model}
        </div>

        {battery && (
          <div className={solarPackageProduct}>
            <b>1 x</b> {battery.brand} {battery.model}
          </div>
        )}

        <span className={solarPackageDiscount}>
          {translate.PRICE}: {priceWithInstallation}{' '}
          <Tooltip title={translate.WITHOUT_INSTALLATION}>({priceWithoutInstallation})</Tooltip>
        </span>

        <span className={solarPackageDiscount}>
          {translate.DISCOUNT}: {formatCurrency(discount)}
          <Tooltip title={translate.WITHOUT_INSTALLATION}>({formatCurrency(discountWithoutInstallation)})</Tooltip>
        </span>

        <Button disabled={selected} onClick={onSelectPackage}>
          {selected ? translate.SELECTED : translate.SELECT}
        </Button>
      </div>
    </Card>
  );
};
